<template>
  <div class="px-4 py-2 border-b dark:border-gray-700">
    <div class="flex items-center justify-between">
      <div class="flex items-center space-x-2">
        <div
          :class="[
            isActive
              ? 'bg-green-500'
              : notificationPermission === 'denied'
                ? 'bg-red-500'
                : 'bg-yellow-500',
            'w-2 h-2 rounded-full'
          ]"
        ></div>
        <span class="text-sm text-gray-700 dark:text-gray-300">
          Push Notifications
        </span>
      </div>
      <span class="text-xs text-gray-500 dark:text-gray-400">
        {{ statusText }}
      </span>
    </div>

    <!-- Surface the reason setup failed, otherwise a failed attempt is
         indistinguishable from never having tried. -->
    <p v-if="error" class="mt-2 text-xs text-red-600 dark:text-red-400">
      {{ error }}
    </p>

    <!-- Enable button for when notifications are not active -->
    <div v-if="showEnableButton" class="mt-2">
      <button
        @click="handleEnable"
        :disabled="isLoading"
        class="w-full text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {{ isLoading ? 'Setting up...' : 'Enable Alerts' }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { usePushNotifications } from '@/composables/usePushNotifications';

const {
  isSupported,
  notificationPermission,
  isSubscribed,
  isRegisteredWithServer,
  isActive,
  isLoading,
  error,
  initialize,
  enableNotifications,
  ensureSubscribed
} = usePushNotifications();

const statusText = computed(() => {
  if (!isSupported.value) return 'Not supported';
  if (notificationPermission.value === 'denied') return 'Blocked';
  if (notificationPermission.value === 'default') return 'Not enabled';
  if (isActive.value) return 'Active';
  if (isSubscribed.value && !isRegisteredWithServer.value) return 'Not registered';
  return 'Setup needed';
});

const showEnableButton = computed(() => {
  return isSupported.value &&
         notificationPermission.value !== 'denied' &&
         !isActive.value;
});

onMounted(async () => {
  await initialize();

  // Permission already granted but no working subscription is a recoverable state -
  // repair it silently rather than waiting for the user to notice the prompt again.
  await ensureSubscribed();
});

async function handleEnable() {
  await enableNotifications();
}
</script>
